import { InfiniteData, QueryClient } from '@tanstack/react-query';

import DiseaseEncyclopediaModel from '@/entities/disease-encyclopedia/model/diseaseEncyclopedia';
import { SearchDiseaseEncyclopediaByCursorScorePageQuery } from '@/types/bff/gql-schema';
import { MagazineType } from '@/types/magazine';
import { useSearchDiseaseEncyclopediaByCursorScorePageQuery } from '../search.gql.impl';
import useGetKeywordSearchDiseaseEncyclopediaInfiniteQuery from './useGetKeywordSearchDiseaseEncyclopediaInfiniteQuery';

const parseDiseaseEncyclopedia = (
  data: Array<SearchDiseaseEncyclopediaByCursorScorePageQuery>
): Array<DiseaseEncyclopediaModel> => {
  if (data) {
    return data
      .flatMap((page) => page.searchDiseaseEncyclopediaByCursorScorePage.items)
      .map((diseaseEncyclopedia) => {
        return new DiseaseEncyclopediaModel({
          publicId: diseaseEncyclopedia.publicId,
          title: diseaseEncyclopedia.title,
          titleImageUrl: diseaseEncyclopedia.titleImageUrl ?? undefined,
          content: diseaseEncyclopedia.content,
        });
      });
  }
  return [] as Array<DiseaseEncyclopediaModel>;
};

interface GetKeywordSearchDiseaseEncyclopediaInfiniteQuery {
  diseaseEncyclopediaData: InfiniteData<DiseaseEncyclopediaModel> | undefined;
  isDiseaseEncyclopediaLoading: boolean;
  hasDiseaseEncyclopediaNextPage?: boolean;
  fetchDiseaseEncyclopediaNextPage: () => void;
}

interface Props {
  keyword: string;
  size?: number;
  contentType?: MagazineType;
  enabled?: boolean;
}

const DISEASE_ENCYCLOPEDIA_SIZE = 20;

const useGetKeywordSearchDiseaseEncyclopediaInfinite = ({
  keyword,
  size = DISEASE_ENCYCLOPEDIA_SIZE,
  enabled = true,
}: Props): GetKeywordSearchDiseaseEncyclopediaInfiniteQuery => {
  const {
    data: diseaseEncyclopediaData,
    isLoading: isDiseaseEncyclopediaLoading,
    hasNextPage: hasDiseaseEncyclopediaNextPage,
    fetchNextPage: fetchDiseaseEncyclopediaNextPage,
  } = useGetKeywordSearchDiseaseEncyclopediaInfiniteQuery(
    {
      request: {
        keyword,
        limit: size,
      },
    },
    {
      select: (data) => {
        return {
          pages: parseDiseaseEncyclopedia(data.pages),
          pageParams: data.pageParams,
        };
      },
      enabled: enabled && !!keyword && keyword.length > 0,
    }
  );

  return {
    diseaseEncyclopediaData,
    isDiseaseEncyclopediaLoading,
    hasDiseaseEncyclopediaNextPage,
    fetchDiseaseEncyclopediaNextPage,
  };
};

export default useGetKeywordSearchDiseaseEncyclopediaInfinite;

useGetKeywordSearchDiseaseEncyclopediaInfinite.prefetchQuery = async (
  queryClient: QueryClient,
  params: Omit<Props, 'enabled'> & { limit: number }
) => {
  await queryClient.prefetchInfiniteQuery(
    useSearchDiseaseEncyclopediaByCursorScorePageQuery.getKey({
      request: params,
    }),
    useSearchDiseaseEncyclopediaByCursorScorePageQuery.fetcher({
      request: params,
    })
  );
};
